import React from 'react';
import { capitalize } from 'lodash';
import styled from 'styled-components';

import {
  AI_PROMPT_PUBLISHED_FILTER_OPTIONS,
  AI_PROMPT_TARGET_OBJECT_FILTER_OPTIONS,
  AI_PROMPT_TARGET_OBJECTS,
  AI_PROMPT_TYPE_FILTER_OPTIONS,
} from 'constants/aiPrompts';
import { EPISODE, PLAN_TYPE } from 'constants/classifications';
import { AdminListPageConfig, AdminListPageRenderer, useAdminListPage } from 'hooks/useAdminListPage';
import { simpleDateAtTime } from 'lib/date';
import type { AIPrompt } from 'models/flexible/AIPrompt';
import { Classification } from 'models/flexible/Classification';
import { GroupType } from 'models/flexible/GroupType';
import { PERMISSIONS } from 'models/flexible/Profile';
import { adminAIPromptsQueryKeys, adminIndexAIPrompts, deleteAIPrompt } from 'services/api/admin/aiPrompts';
import { adminIndexClassifications } from 'services/api/admin/classifications';
import { adminIndexClients } from 'services/api/admin/clients';
import { indexGroupTypes } from 'services/api/admin/groupTypes';
import type { AdminAIPromptIndexFields, AdminClassificationIndexFields } from 'services/api/fields';
import type { GroupTypeFields } from 'services/api/fields';
import { colors } from 'styles/theme/colors';
import CopyIcon from 'svg/CopyIcon';
import DeactivateIcon from 'svg/DeactivateIcon';
import EyeIcon from 'svg/EyeIcon';
import PencilIcon from 'svg/PencilIcon';

import AIPromptPublishModal from './AIPromptPublishModal';

const AI_PROMPT_INCLUDES = [
  'client',
  'groupTypes',
  'planTypeClassifications',
  'episodeClassifications',
  'config',
  'latestProcessPromptStatus',
] as const;

type AIPromptRow = AIPrompt<AdminAIPromptIndexFields<(typeof AI_PROMPT_INCLUDES)[number]>>;

const config: AdminListPageConfig<AIPromptRow> = {
  title: 'AI Prompts',
  queryKey: adminAIPromptsQueryKeys.index(),
  fetchFn: (params, signal) =>
    adminIndexAIPrompts({ ...params, 'target_classname.not': 'Summarization' }, signal, [...AI_PROMPT_INCLUDES]),
  includes: AI_PROMPT_INCLUDES,
  fetchParams: {
    defaultSort: 'publishedAt desc',
  },
  showResultCount: true,

  addButton: {
    label: 'Add AI Prompt',
    permission: 'adminAIPromptCreate',
    path: '/admin/ai-prompts/new',
  },

  filters: [
    {
      key: 'targetClassname',
      label: 'Target Object',
      type: 'dropdown',
      staticOptions: AI_PROMPT_TARGET_OBJECT_FILTER_OPTIONS,
      getOptionLabel: (o) => o.name,
      getOptionValue: (o) => o.id,
    },
    {
      key: 'client',
      label: 'Client',
      type: 'dropdown',
      fetchConfig: {
        queryKey: ['adminClients', 'aiPrompts', 'filter'],
        fetchFn: adminIndexClients,
        params: { sortBy: 'name asc' },
      },
      getOptionLabel: (o) => o.name,
      getOptionValue: (o) => o.id,
    },
    {
      key: 'groupType',
      label: 'Levels of Care',
      type: 'multi_select',
      fetchConfig: {
        queryKey: ['adminGroupTypes', 'aiPrompts', 'filter'],
        fetchFn: indexGroupTypes,
        params: { sortBy: 'displayName asc' },
      },
      getOptionLabel: (o) => o.displayName,
      getOptionValue: (o) => o.id,
    },
    {
      key: 'planTypeClassifications',
      label: 'Plan Type',
      type: 'multi_select',
      fetchConfig: {
        queryKey: ['adminClassifications', 'planType', 'aiPrompts', 'filter'],
        fetchFn: adminIndexClassifications,
        params: { type: PLAN_TYPE, sortBy: 'name asc' },
      },
      getOptionLabel: (o) => o.name,
      getOptionValue: (o) => o.id,
    },
    {
      key: 'episodeClassifications',
      label: 'Episode Type',
      type: 'multi_select',
      fetchConfig: {
        queryKey: ['adminClassifications', 'episode', 'aiPrompts', 'filter'],
        fetchFn: adminIndexClassifications,
        params: { type: EPISODE, sortBy: 'name asc' },
      },
      getOptionLabel: (o) => o.name,
      getOptionValue: (o) => o.id,
    },
    {
      key: 'type',
      label: 'Prompt Type',
      type: 'dropdown',
      staticOptions: AI_PROMPT_TYPE_FILTER_OPTIONS,
      getOptionLabel: (o) => o.name,
      getOptionValue: (o) => o.id,
    },
    {
      key: 'isPublished',
      label: 'Published',
      type: 'dropdown',
      staticOptions: AI_PROMPT_PUBLISHED_FILTER_OPTIONS,
      getOptionLabel: (o) => o.name,
      getOptionValue: (o) => o.id,
    },
  ],

  columns: [
    {
      id: 'name',
      header: 'Name',
      render: (row) => row.name || '—',
    },
    {
      id: 'targetClassname',
      header: 'Target Object',
      render: (row) => AI_PROMPT_TARGET_OBJECTS.find(({ id }) => id === row.targetClassname)?.name || '—',
    },
    {
      id: 'client',
      header: 'Client',
      render: (row) => <strong>{row.client?.name || '—'}</strong>,
    },
    {
      id: 'levelsOfCare',
      header: 'Levels of Care',
      sortable: false,
      render: (row) => row.groupTypes.map((gt: GroupType<GroupTypeFields>) => gt.displayName).join(', ') || '—',
    },
    {
      id: 'planType',
      header: 'Plan Type',
      sortable: false,
      render: (row) =>
        row.planTypeClassifications.map((c: Classification<AdminClassificationIndexFields>) => c.name).join(', ') ||
        '—',
    },
    {
      id: 'episodeType',
      header: 'Episode Type',
      sortable: false,
      render: (row) =>
        row.episodeClassifications.map((c: Classification<AdminClassificationIndexFields>) => c.name).join(', ') || '—',
    },
    {
      id: 'type',
      header: 'Prompt Type',
      render: (row) => capitalize(row.type || '—'),
    },
    {
      id: 'status',
      header: 'Published?',
      sortable: false,
      render: (row) => (
        <StatusCell>
          <StatusDot $on={row.isPublished} />
          {row.isPublished ? 'Yes' : 'No'}
        </StatusCell>
      ),
    },
    {
      id: 'publishedAt',
      header: 'Published At',
      render: (row) => (row.publishedAt ? simpleDateAtTime(row.publishedAt) : '—'),
    },
  ],

  rowActions: [
    {
      label: 'Edit',
      icon: <PencilIcon width={18} height={18} />,
      permission: 'adminAIPromptEdit',
      onClick: (row, { navigate }) => navigate(`/admin/ai-prompts/${row.id}/edit`),
    },
    {
      label: 'View',
      icon: <EyeIcon width={18} height={18} />,
      visible: (row, ctx) => !ctx.profile.has(PERMISSIONS.adminAIPromptEdit),
      onClick: (row, { navigate }) => navigate(`/admin/ai-prompts/${row.id}/edit`),
    },
    {
      label: 'Publish',
      visible: (row, ctx) => ctx.profile.has(PERMISSIONS.adminAIPromptPublish) && !row.isPublished,
      disabled: (row) => !row.isProcessPromptComplete,
      onClick: (row, { openModal }) => openModal('publish', row),
    },
    {
      label: 'Copy',
      icon: <CopyIcon width={18} height={18} />,
      visible: (row, ctx) => ctx.profile.has(PERMISSIONS.adminAIPromptCreate) && !!row.isPublished,
      onClick: (row, { navigate }) => navigate(`/admin/ai-prompts/${row.id}/edit?copy=true`),
    },
    {
      label: 'Archive',
      icon: <DeactivateIcon width={18} height={18} />,
      danger: true,
      visible: (row, ctx) => !row.isPublished && !row.archived && ctx.profile.has(PERMISSIONS.adminAIPromptDelete),
      onClick: (row, { openModal }) => openModal('archive', row),
    },
  ],

  modals: {
    archive: {
      actionText: 'archive',
      objType: 'AI Prompt',
      onConfirm: async (row) => {
        await deleteAIPrompt(row.id);
      },
    },
  },

  customModals: {
    publish: {
      render: (row, show, onClose, ctx) => (
        <AIPromptPublishModal
          isOpen={show}
          aiPrompt={row}
          onCancel={onClose}
          onComplete={() => {
            onClose();
            ctx.refetch();
          }}
        />
      ),
    },
  },
};

export default function AIPrompts() {
  const state = useAdminListPage(config);

  return <AdminListPageRenderer config={config} state={state} />;
}

const StatusCell = styled.div`
  display: flex;
  align-items: center;
`;

const StatusDot = styled.div<{ $on: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${({ $on }) => ($on ? colors.accentGreen : colors.black25)};
  margin-right: 12px;
`;
